/**
 * Create something like MUI theme?
 *
 * See: https://github.com/mui-org/material-ui/blob/master/packages/material-ui/src/styles/createMuiTheme.js
 * See: https://github.com/styled-components/polished
 * See: https://github.com/styled-components/styled-theming
 *
 */

import { rem, rgba, lighten, getContrast, darken } from 'polished';

export const breakpoints = {
  small: 0,
  smallmedium: 420,
  medium: 640,
  mediumlarge: 820,
  large: 1024,
  xlarge: 1280,
  xxlarge: 1440,
};

export const breakpointClasses = [
  'small',
  'smallmedium',
  'medium',
  'mediumlarge',
  'large',
  'xlarge',
]; // Separate array to be able to define more breakpoints that not currently will be used

function createBreakpoints() {
  const mediaQueries = {};

  // Object.keys(breakpointClasses).forEach((breakpoint) => {
  breakpointClasses.forEach((breakpoint) => {
    mediaQueries[breakpoint] = `(min-width: ${breakpoints[breakpoint]}px)`;
  });

  return mediaQueries;
}

export const colors = {
  primary: '#8A7E46',
  secondary: '#3D3719',
  third: '#D6C783',
  fourth: '#3D3719',
  success: '#919645',
  warning: '#D6C36D',
  alert: '#8A433F',
  cta: '#8A7E46',
  alt: '#3498DB',
  black: '#1A1A1A',
  white: '#ffffff',
  greyLight: '#F3F3F3',
  grey: '#5D5D5D',
  greyDark: '#424242',
  lightBlack: '#2f2f2f',
  border: '#5D5D5D',
  borderNegative: '#ffffff',
  fadedGray: rgba('#E9E9E9', 0.5),
  fadedText: '#555555',
  fadedTextNegative: '#B2B2B2',
  backgroundDarken: rgba('#000', 0.4),
};

const buttonColors = {
  primary: colors.primary,
  secondary: colors.secondary,
  third: colors.third,
  success: colors.success,
  warning: colors.warning,
  alert: colors.alert,
  cta: colors.cta,
  ctaAlt: colors.fourth,
  alt: colors.alt,
  black: colors.black,
  white: colors.white,
  grey: colors.grey,
};

const theme = {
  mode: 'light',
  global: {
    radius: '999px',
    menuPadding: '0.6rem 1rem',
    textDirection: 'ltr',
    transition: {
      duration: '0.15s',
      timing: 'ease',
    },
    border: {
      color: colors.border,
      width: '1px',
      style: 'solid',
    },
  },
  palette: {
    background: {
      body: colors.black,
      content: colors.white,
      accent: colors.cta,
      dark: colors.lightBlack,
      light: colors.greyLight,
    },
    text: {
      primary: colors.black,
      negative: colors.white,
    },
    colors,
    social: {
      facebook: '#3b5998',
      twitter: '#1da1f2',
      pinterest: '#bd081c',
      youtube: '#cd201f',
      instagram: '#e1306c',
      vimeo: '#1ab7ea',
      linkedin: '#0077b5',
      googleplus: '#dd4b39',
      reddit: '#ff4500',
      whatsapp: '#25d366',
    },
    buttons: buttonColors,
  },
  spacing: {
    gutter: rem(20),
    columnGutter: rem(20 / 2),
    // gutter: {
    //   small: rem(20),
    //   large: rem(30),
    // }
  },
  grid: {
    columns: 16,
    maxWidth: rem(1280),
    maxWidthText: rem(730),
    breakpoints: createBreakpoints(),
  },
  typography: {
    global: {
      fontSize: '100%',
      lineHeight: 1.5,
      rendering: 'optimizeLegibility',
    },
    fonts: {
      primary: "'Roboto', sans-serif",
      heading: "'EB Garamond', serif",
      alt: "'EB Garamond', serif",
      button: "'EB Garamond', serif",
    },
    weights: {
      primary: {
        normal: '300',
        bold: '500',
      },
      alt: {
        normal: '400',
        bold: '500',
      },
      heading: {
        normal: '400',
        bold: '500',
      },
      button: {
        normal: '400',
        bold: '500',
      },
    },
    heading: {
      // lineHeight: 1.32,
      lineHeight: 1.1,
      // marginBottom: '0.7em',
      marginBottom: '0.44em',
      textTransform: 'none',
      headingSizes: {
        small: {
          h1: rem(28),
          h2: rem(28),
          h3: rem(22),
          h4: rem(20),
          h5: rem(18),
          h6: rem(16),
        },
        mediumlarge: {
          h1: rem(30),
          h2: rem(30),
          h3: rem(22),
          h4: rem(20),
          h5: rem(18),
          h6: rem(16),
        },
        large: {
          h1: rem(32),
          h2: rem(32),
          h3: rem(24),
          h4: rem(20),
          h5: rem(18),
          h6: rem(16),
        },
      },
    },
    paragraph: {
      fontSize: rem(17),
      lineHeight: 1.7,
      marginBottom: '1.14rem',
    },
    anchor: {
      default: colors.primary,
      hover: lighten(0.2, colors.primary),
      decoration: 'none',
    },
    button: {
      letterSpacing: '0.05em',
      textTransform: 'uppercase',
      fontWeight: '500',
      buttonSizes: {
        small: rem(13),
        default: rem(16),
        large: rem(18),
      },
    },
  },
  components: {
    button: {
      radius: '0px',
      padding: '0.93rem 2.45em',
      colorMultiplier: 0.04,
    },
    drawer: {
      background: colors.white,
      padding: '0.5rem 1rem',
      item: {
        color: colors.black,
        hover: lighten(0.3, colors.primary),
        padding: '0.4rem 1.15rem',
        fontSize: rem(15.5),
      },
    },
    menu: {
      item: {
        color: colors.black,
        hover: lighten(0.3, colors.black),
        colorNegative: colors.white,
        hoverNegative: darken(0.2, colors.white),
        padding: '0.75rem 0.85rem',
        fontSize: rem(18),
        letterSpacing: '0.01em',
        textTransform: 'uppercase',
        fontWeight: '500',
      },
      itemVertical: {
        color: colors.black,
        hover: lighten(0.2, colors.black),
        colorNegative: colors.white,
        hoverNegative: darken(0.2, colors.white),
        padding: '0.4em 1.15rem',
        fontSize: rem(16),
        letterSpacing: '0.01em',
        textTransform: 'uppercase',
        fontWeight: '600',
      },
    },
    input: {
      radius: '0',
      padding: '0.5rem',
      borderColor: colors.grey,
      backgroundColor: colors.greyLight,
      backgroundColorActive: colors.greyLight,
      color: colors.black,
    },
  },
};

/**
 * Helper method for easier use of breakpoint output in styled-component styles
 */
export const breakpoint = (size) => {
  return `@media ${theme.grid.breakpoints[size]}`;
};

/**
 * Helper method to generate responsive heading sizing from objects by breakpoint
 */
export const headingSizes = (sizes) => {
  let output = '';

  Object.keys(sizes).forEach((breakpointKey) => {
    let breakpointSizes = '';

    Object.keys(sizes[breakpointKey]).forEach((selector) => {
      breakpointSizes += `
        ${selector} {
          font-size: ${sizes[breakpointKey][selector]};
        }\n`;
    });

    output += `${breakpoint(breakpointKey)} {
      ${breakpointSizes}
    }\n`;
  });

  return output;
};

/**
 * Helper method to generate button variants
 */
export const buttonVariants = (variant) => {
  let output = '';

  // Sizes
  Object.keys(theme.typography.button.buttonSizes).forEach((key) => {
    // skip default size
    if (key !== 'default') {
      output += `
        &.${key} {
          font-size: ${theme.typography.button.buttonSizes[key]};
        }\n`;
    }
  });

  // Colors
  if (variant === 'hollow') {
    Object.keys(buttonColors).forEach((color) => {
      const contrastRatio = getContrast(
        buttonColors[color],
        theme.palette.background.content,
      );
      const contrastRatioHover = getContrast(buttonColors[color], '#fff');

      const textColor = contrastRatio > 2.1 ? '#fff' : '#000';
      const textColorHover = contrastRatioHover > 2.1 ? '#fff' : '#000';

      output += `
        &.${color} {
          color: ${textColor};
          border-color: ${buttonColors[color]};

          &:hover {
            background-color: ${buttonColors[color]};
            color: ${textColorHover};
          }
        }\n`;
    });
  } else {
    Object.keys(buttonColors).forEach((color) => {
      const contrastRatio = getContrast(buttonColors[color], '#fff');
      const textColor = contrastRatio > 2.1 ? '#fff' : '#000';

      output += `
        &.${color} {
          background-color: ${buttonColors[color]};
          color: ${textColor};

          &:hover {
            background-color: ${lighten(
              theme.components.button.colorMultiplier,
              buttonColors[color],
            )};
          }
        }\n`;
    });
  }

  return output;
};

export default theme;
